/**
 * SubscriptionsTable - Tabela reutilizável de assinaturas/recorrências
 *
 * Extraída de `src/pages/financial/FinancialSubscriptions.tsx` para permitir
 * o reuso da mesma listagem (header + linhas) no modal de cliente
 * (sub-aba Recorrência) sem duplicar layout/colunas.
 *
 * @version 1.0.0
 * RELEASE: 2026-06-10 v1.0.0 - Extração da tabela + SubscriptionRow para reuso
 */
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Repeat, Eye, MoreHorizontal, PauseCircle, PlayCircle, XCircle } from 'lucide-react';
import { cn, parseDateLocal } from '@/lib/utils';
import { formatDocumentForDisplay } from '@/lib/input-masks';
import { TIPOS_PAGAMENTO } from '@/hooks/useFinanceiroMeiosPagamento';
import { PaginatedAssinatura } from '@/hooks/useFinanceiroAssinaturasPaginated';
import { useAssinaturaActions } from '@/hooks/useAssinaturaActions';
// v1.1.0: controle de permissão granular (financial_charges) para ações da linha
import { usePermissions } from '@/hooks/usePermissions';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

/* ==========================================================================
 * Helpers e mapas de label/cores (compartilhados)
 * ========================================================================== */

/** Mapa de status com cores semânticas */
export const statusBadgeColors: Record<string, string> = {
  ativa: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  pausada: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  cancelada: 'bg-gray-200 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
  finalizada: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
};

/** Labels de status */
export const statusLabels: Record<string, string> = {
  ativa: 'Ativa',
  pausada: 'Pausada',
  cancelada: 'Cancelada',
  finalizada: 'Finalizada',
};

/** Cor da barra lateral por status */
export function getStatusBarColor(status: string): string {
  switch (status) {
    case 'ativa':
      return 'bg-green-500';
    case 'pausada':
      return 'bg-amber-500';
    case 'cancelada':
      return 'bg-gray-400';
    case 'finalizada':
      return 'bg-blue-500';
    default:
      return 'bg-muted-foreground/40';
  }
}

/** Labels de frequência */
export const FREQ_LABEL: Record<string, string> = {
  semanal: 'Semanal',
  quinzenal: 'Quinzenal',
  mensal: 'Mensal',
  bimestral: 'Bimestral',
  trimestral: 'Trimestral',
  semestral: 'Semestral',
  anual: 'Anual',
  bienal: 'Bienal',
};

/** Dias por ciclo conforme frequência */
const FREQ_DIAS: Record<string, number> = {
  semanal: 7,
  quinzenal: 15,
  mensal: 30,
  bimestral: 60,
  trimestral: 90,
  semestral: 180,
  anual: 365,
  bienal: 730,
};

/**
 * Calcula a data fim somando (ciclos_total - 1) períodos ao primeiro vencimento.
 * Retorna null se faltarem dados ou frequência não estiver mapeada.
 */
export function calcEndDate(
  primeiroVencimento: string | null,
  frequencia: string,
  ciclosTotal: number | null,
): Date | null {
  if (!primeiroVencimento || !ciclosTotal || ciclosTotal < 1) return null;
  const dias = FREQ_DIAS[frequencia];
  if (!dias) return null;
  const base = parseDateLocal(primeiroVencimento);
  const fim = new Date(base);
  fim.setDate(fim.getDate() + dias * (ciclosTotal - 1));
  return fim;
}

/** Formata valor monetário em BRL/moeda informada */
export function formatSubscriptionCurrency(v: number, moeda = 'BRL'): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: moeda || 'BRL',
  }).format(v || 0);
}

/* ==========================================================================
 * SubscriptionRow - Linha da tabela (com ações inline)
 * ========================================================================== */

interface SubscriptionRowProps {
  assinatura: PaginatedAssinatura;
  onOpen: () => void;
  formatCurrency?: (v: number, moeda?: string) => string;
  /** Oculta a coluna do cliente (usado quando lista já é por cliente) */
  hideCustomer?: boolean;
  /** v1.1.0: permite pausar/reativar (financial_charges.can_edit). Default: resolvido pelo hook */
  canEdit?: boolean;
  /** v1.1.0: permite cancelar (financial_charges.can_delete). Default: resolvido pelo hook */
  canDelete?: boolean;
}

export function SubscriptionRow({
  assinatura: a,
  onOpen,
  formatCurrency = formatSubscriptionCurrency,
  hideCustomer = false,
  canEdit,
  canDelete,
}: SubscriptionRowProps) {
  const { pausar, cancelar, isBusy } = useAssinaturaActions(a.id);
  // v1.1.0: fallback de permissão quando o container não informa explicitamente
  const perms = usePermissions();
  const allowEdit = canEdit ?? (perms.isAdmin || perms.canEdit('financial_charges'));
  const allowDelete = canDelete ?? (perms.isAdmin || perms.canDelete('financial_charges'));
  const isAtiva = a.status === 'ativa';
  const isCancelada = a.status === 'cancelada' || a.status === 'finalizada';

  return (
    <TableRow className="cursor-pointer hover:bg-muted/40 group" onClick={onOpen}>
      <TableCell className="p-0 w-2">
        <div className={cn('w-1 h-full min-h-[40px] rounded-full', getStatusBarColor(a.status))} />
      </TableCell>

      <TableCell className="py-2">
        <span className="font-mono text-[11px] text-muted-foreground">
          {a.id.substring(0, 8).toUpperCase()}
        </span>
      </TableCell>

      {/* Dt. Criação */}
      <TableCell className="py-2">
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          {format(new Date(a.created_at), 'dd/MM/yy', { locale: ptBR })}
        </span>
      </TableCell>

      {!hideCustomer && (
        <TableCell className="py-2">
          <div className="min-w-0">
            <p className="text-sm font-medium truncate max-w-[160px]">
              {a.customer_name || 'Sem cliente'}
            </p>
            {a.customer_document && (
              <p className="text-xs text-muted-foreground truncate max-w-[160px]">
                {formatDocumentForDisplay(a.customer_document)}
              </p>
            )}
          </div>
        </TableCell>
      )}

      <TableCell className="py-2">
        <p className="text-sm truncate max-w-[200px]">{a.descricao || '—'}</p>
      </TableCell>

      <TableCell className="py-2">
        <Badge variant="outline" className="text-[11px]">
          {FREQ_LABEL[a.frequencia] || a.frequencia}
        </Badge>
      </TableCell>

      <TableCell className="py-2">
        <div className="flex items-center gap-1.5 whitespace-nowrap">
          <Badge variant="outline" className="text-[11px] font-mono">
            {a.ciclos_pagos}/{a.ciclos_total ?? '∞'}
          </Badge>
          {a.ciclos_pendentes > 0 && (
            <span className="text-[10px] text-muted-foreground">({a.ciclos_pendentes} pend.)</span>
          )}
        </div>
      </TableCell>

      <TableCell className="py-2">
        <span className="text-sm font-mono font-medium">
          {formatCurrency(a.valor_total, a.moeda)}
        </span>
      </TableCell>

      <TableCell className="py-2">
        <span
          className={cn(
            'text-[11px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap',
            statusBadgeColors[a.status] || 'bg-gray-100 text-gray-800',
          )}
        >
          {statusLabels[a.status] || a.status}
        </span>
      </TableCell>

      <TableCell className="py-2">
        {a.proximo_vencimento ? (
          <span className="text-xs text-muted-foreground">
            {format(parseDateLocal(a.proximo_vencimento), 'dd/MM/yy', { locale: ptBR })}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground/50">—</span>
        )}
      </TableCell>

      {/* Dt. Fim */}
      <TableCell className="py-2">
        {(() => {
          if (a.tipo_prazo === 'indeterminado') {
            return (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-xs text-muted-foreground/60 cursor-help">∞</span>
                </TooltipTrigger>
                <TooltipContent>Prazo indeterminado</TooltipContent>
              </Tooltip>
            );
          }
          const fim = calcEndDate(a.primeiro_vencimento, a.frequencia, a.ciclos_total);
          if (!fim) return <span className="text-xs text-muted-foreground/50">—</span>;
          return (
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              {format(fim, 'dd/MM/yy', { locale: ptBR })}
            </span>
          );
        })()}
      </TableCell>

      <TableCell className="py-2">
        {a.tipo_pagamento ? (
          <span className="text-xs text-muted-foreground whitespace-nowrap">
            {TIPOS_PAGAMENTO.find(t => t.value === a.tipo_pagamento)?.label || a.tipo_pagamento}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground/50">—</span>
        )}
      </TableCell>

      <TableCell className="py-2">
        {a.conta_corrente_nome ? (
          <span className="text-xs text-muted-foreground truncate max-w-[120px] block">
            {a.conta_corrente_nome}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground/50">—</span>
        )}
      </TableCell>

      <TableCell className="py-2" onClick={e => e.stopPropagation()}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <MoreHorizontal className="h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuItem onClick={onOpen}>
              <Eye className="h-4 w-4 mr-2 text-muted-foreground" />
              Ver Detalhes
            </DropdownMenuItem>
            {!isCancelada && (allowEdit || allowDelete) && <DropdownMenuSeparator />}
            {!isCancelada && allowEdit && (
              <>
                <DropdownMenuItem onClick={() => pausar.mutate(isAtiva)} disabled={isBusy}>
                  {isAtiva ? (
                    <>
                      <PauseCircle className="h-4 w-4 mr-2 text-amber-600" />
                      Pausar Recorrência
                    </>
                  ) : (
                    <>
                      <PlayCircle className="h-4 w-4 mr-2 text-green-600" />
                      Reativar Recorrência
                    </>
                  )}
                </DropdownMenuItem>
              </>
            )}
            {!isCancelada && allowDelete && (
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onClick={() => cancelar.mutate(false)}
                disabled={isBusy}
              >
                <XCircle className="h-4 w-4 mr-2" />
                Cancelar (apenas pendentes)
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
}

/* ==========================================================================
 * SubscriptionsTable - Tabela completa (header + body) reutilizável
 * ========================================================================== */

interface SubscriptionsTableProps {
  assinaturas: PaginatedAssinatura[];
  isLoading?: boolean;
  onRowClick: (a: PaginatedAssinatura) => void;
  formatCurrency?: (v: number, moeda?: string) => string;
  /** Oculta a coluna de cliente (quando já estamos no contexto de um cliente) */
  hideCustomer?: boolean;
  /** Mensagem custom de empty state */
  emptyMessage?: string;
  emptyHint?: string;
  /** v1.1.0: permissão de edição (pausar/reativar) */
  canEdit?: boolean;
  /** v1.1.0: permissão de exclusão (cancelar) */
  canDelete?: boolean;
}

export function SubscriptionsTable({
  assinaturas,
  isLoading = false,
  onRowClick,
  formatCurrency = formatSubscriptionCurrency,
  hideCustomer = false,
  emptyMessage = 'Nenhuma recorrência encontrada',
  emptyHint,
  canEdit,
  canDelete,
}: SubscriptionsTableProps) {
  if (isLoading) {
    return (
      <div className="space-y-2 p-4">
        {[1, 2, 3, 4, 5].map(i => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (assinaturas.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <Repeat className="h-12 w-12 text-muted-foreground/40" />
        <div className="text-center">
          <h3 className="font-medium text-sm">{emptyMessage}</h3>
          {emptyHint && <p className="text-xs text-muted-foreground mt-1">{emptyHint}</p>}
        </div>
      </div>
    );
  }

  return (
    <Table scrollViewport containerClassName="h-full" className={cn(hideCustomer ? 'min-w-[950px]' : 'min-w-[1100px]')}>
      <TableHeader>
        <TableRow className="hover:bg-transparent">
          <TableHead className="w-2 p-0" />
          <TableHead className="text-xs font-medium">Ref.</TableHead>
          <TableHead className="text-xs font-medium">Dt. Criação</TableHead>
          {!hideCustomer && <TableHead className="text-xs font-medium">Cliente</TableHead>}
          <TableHead className="text-xs font-medium">Descrição</TableHead>
          <TableHead className="text-xs font-medium">Frequência</TableHead>
          <TableHead className="text-xs font-medium">Ciclos</TableHead>
          <TableHead className="text-xs font-medium">Valor</TableHead>
          <TableHead className="text-xs font-medium">Status</TableHead>
          <TableHead className="text-xs font-medium">Próx. Venc.</TableHead>
          <TableHead className="text-xs font-medium">Dt. Fim</TableHead>
          <TableHead className="text-xs font-medium">Meio Pgto</TableHead>
          <TableHead className="text-xs font-medium">Conta</TableHead>
          <TableHead className="w-10" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {assinaturas.map(a => (
          <SubscriptionRow
            key={a.id}
            assinatura={a}
            onOpen={() => onRowClick(a)}
            formatCurrency={formatCurrency}
            hideCustomer={hideCustomer}
            canEdit={canEdit}
            canDelete={canDelete}
          />
        ))}
      </TableBody>
    </Table>
  );
}
